import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import { LazyTippy } from 'app/components';
import { i18n } from 'app/utils/i18n';
import Modal from 'app/components/Modal';
import { dispatchMessage, EventType } from 'app/useToasts';
import { useGetPermissions, useAddPermissionToUser, useRemovePermissionFromUser } from 'app/api/permission-queries';
import { useUsersInOrganization } from 'app/api/auth/user-queries';
import { UserContext } from 'app/state/contexts/UserContext';
import { AddUser } from './AddUser/AddUser';
import { RemoveUser } from './RemoveUser/RemoveUser';

const DocumentPermissions = () => {
  const { documentId } = useParams();
  const { selectedOrganization } = useContext(UserContext);
  const { data: permissions } = useGetPermissions(documentId);
  const { data: users = [] } = useUsersInOrganization(selectedOrganization);
  const { mutate: addPermission } = useAddPermissionToUser();
  const { mutate: removePermission } = useRemovePermissionFromUser();
  const [showModal, setShowModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  //TODO Maybe fetch this list from backend, gaby-documents or client-config
  const AVAILABLE_DOCUMENT_PERMISSIONS = [
    { title: i18n('document-permissions.permission.project-manager'), permission: 'ADMIN' },
    { title: i18n('document-permissions.permission.design'), permission: 'DESIGN' },
    { title: i18n('document-permissions.permission.coder'), permission: 'CODER' },
    { title: i18n('document-permissions.permission.reference-data'), permission: 'DB' },
  ];

  const addUserPermission = (userName, permission) => {
    if (!documentId) return;

    const user = users.find((u) => u.userName === userName);
    if (!user) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('document-permissions.add-user.not-found'),
        removeTime: 4000,
      });
      return;
    }

    addPermission({
      documentId,
      permission,
      userId: user.id,
    });
  };

  const handleRemoveUserPermission = (event) => {
    event.preventDefault();
    if (!documentId || !userToRemove) return;

    removePermission({
      documentId,
      permission: userToRemove.permission,
      userId: userToRemove.userId,
    });
    setShowModal(false);
  };

  return (
    <div>
      {AVAILABLE_DOCUMENT_PERMISSIONS.map((permissionRole) => (
        <div key={permissionRole.title}>
          <div className="permission-section-header columns col-1fr-auto">
            <h2 className="permission-title">{permissionRole.title}</h2>
            <LazyTippy
              interactive={true}
              trigger="click"
              offset={[20, 10]}
              placement="bottom"
              content={<AddUser onClick={(userName) => addUserPermission(userName, permissionRole.permission)} />}
            >
              <button className="fab" type="button">
                <i className="fa-regular fa-plus font-15" />
              </button>
            </LazyTippy>
          </div>
          <div>
            <table className="permissions-table">
              <tbody>
                {!permissions?.userDocumentPermissions
                  ? null
                  : permissions.userDocumentPermissions
                      .filter((userDocumentPermission) => userDocumentPermission.permission === permissionRole.permission)
                      .map((member) => {
                        const user = users.find((user) => user.id === member.userId);
                        if (!user) {
                          return (
                            <tr key={permissionRole.permission + member.userId}>
                              <td>{i18n('common.loading-message')}</td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={user.id}>
                            <td className="columns col-1fr-auto" style={{ cursor: 'default' }}>
                              {user.firstName} {user.lastName}
                              <Tippy
                                interactive={true}
                                offset={[90, -80]}
                                trigger="click"
                                content={
                                  <RemoveUser
                                    onClick={() => {
                                      setShowModal(true);
                                      setUserToRemove({ userId: user.id, userName: user.userName, permission: permissionRole.permission });
                                    }}
                                  />
                                }
                              >
                                <i className="fa-regular fa-ellipsis-v more toggle-icon edit-permission-group" />
                              </Tippy>
                            </td>
                          </tr>
                        );
                      })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Modal titleLabelKey={'document-permissions.remove-user.modal.title'} onClose={() => setShowModal(false)} show={showModal} size="md">
        <form className="form" onSubmit={handleRemoveUserPermission}>
          <div>
            <span className="flex flex-row gap-1">
              <i className="fa-regular fa-trash-can modal-icon" />
              <h3 className="modal-body-title">{i18n('document-permissions.remove-user.modal.title')}</h3>
            </span>
            <p className="modal-body-text">
              {i18n('document-permissions.remove-user.modal.text')} {userToRemove?.userName} ?
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="button clear"
              onClick={() => {
                setShowModal(false);
              }}
            >
              {i18n('common.button.cancel')}
            </button>
            <button type="submit" className="button primary">
              {i18n('document-permissions.remove-user.modal.button.remove')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DocumentPermissions;
